import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { applicationsV2, type BankCheckAuditResult, type BankCheckAuditRow } from "@/features/applications/api/applications.api";
import { useAuth } from "@/features/auth/auth";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

const RESULT_LABELS: Record<BankCheckAuditResult, string> = {
  Match: "Matches",
  ExtraDigits: "Extra digit at the end",
  Mismatch: "Doesn't match",
  Unreadable: "Couldn't read clearly",
  Error: "Couldn't open",
};

const RESULT_TONES: Record<BankCheckAuditResult, string> = {
  Match: "bg-emerald-50 text-emerald-700",
  ExtraDigits: "bg-red-50 text-red-700",
  Mismatch: "bg-red-50 text-red-700",
  Unreadable: "bg-amber-50 text-amber-700",
  Error: "bg-slate-100 text-slate-600",
};

export default function BankCheckAuditPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<BankCheckAuditRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problemsOnly, setProblemsOnly] = useState(true);
  const stop = useRef(false);

  if (user?.role !== "ADMIN") {
    return <Notice tone="warn">Only admins can run the bank check audit.</Notice>;
  }

  async function run() {
    setRows([]);
    setTotal(null);
    setError(null);
    setRunning(true);
    stop.current = false;
    let skip = 0;
    try {
      for (;;) {
        const page = await applicationsV2.bankCheckAudit(skip);
        setTotal(page.total);
        setRows((r) => [...r, ...page.rows]);
        if (page.rows.length === 0 || page.next >= page.total || stop.current) break;
        skip = page.next;
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The audit stopped. Try again.");
    } finally {
      setRunning(false);
    }
  }

  const problems = rows.filter((r) => r.result !== "Match" || !r.routingMatches);
  const shown = problemsOnly ? problems : rows;

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Bank check audit</h1>
        <p className="text-sm text-slate-500 mt-1">
          Re-reads every saved void check and compares it with the account and routing numbers on the application.
          Only the last 4 digits are shown. Open the application to fix a number.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button type="button" onClick={run} disabled={running}>
          {running ? "Checking…" : rows.length > 0 ? "Run again" : "Run audit"}
        </Button>
        {running && (
          <Button type="button" variant="outline" onClick={() => { stop.current = true; }}>Stop</Button>
        )}
        {total !== null && (
          <span className="text-sm text-slate-600">
            Checked {rows.length} of {total} · {problems.length} to review
          </span>
        )}
        {rows.length > 0 && (
          <label className="ml-auto flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />
            Only show ones to review
          </label>
        )}
      </div>

      {error && <Notice tone="error" className="mb-4">{error}</Notice>}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Business</th>
                <th className="px-3 py-2 font-medium">Result</th>
                <th className="px-3 py-2 font-medium">On the application</th>
                <th className="px-3 py-2 font-medium">On the check</th>
                <th className="px-3 py-2 font-medium">Routing</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.applicationId} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <Link to={`/applications/v2/${r.applicationId}`} className="text-blue-600 hover:underline">
                      {r.dbaName || "Untitled"}
                    </Link>
                    <div className="text-xs text-slate-400">{r.status}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", RESULT_TONES[r.result])}>
                      {RESULT_LABELS[r.result]}
                    </span>
                    {r.problem && <div className="mt-1 text-xs text-slate-400">{r.problem}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono">{r.savedAccount ?? "—"}</td>
                  <td className="px-3 py-2 font-mono">{r.readAccount ?? "—"}</td>
                  <td className="px-3 py-2">{r.routingMatches ? "Matches" : <span className="text-red-700">Check it</span>}</td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Nothing to review so far.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
